import { supabase } from '../lib/supabase';
import { todayStr, toAmsterdamISO } from './dateUtils.js';

// Fitness tracking deel 5: hoeveel dagen terug we PT-afspraken meenemen bij
// het bepalen van "niet-gelogde sessies" — geen magic number in de query.
export const UNLOGGED_SESSION_LOOKBACK_DAYS = 30;

function throwIfError(response) {
  if (response.error) throw response.error;
}

// Rollen die de coach_clients-koppelingseis van de RLS overslaan: zij mogen
// voor elke cliënt loggen (zie de schrijfpolicies in
// 20260814110000_self_logging_rls.sql).
const ROLLEN_ZONDER_KOPPELINGSEIS = ['admin_coach', 'superadmin'];

// Gedeelde basis voor zowel het dashboardkaartje als de volledige lijst
// (NietGelogdeSessiesPage): PT-afspraken van de ingelogde coach (als
// aanmaker óf uitvoerder — zelfde `.or()`-patroon als DashboardPage se
// eigen "vandaag/morgen"-query) die al voorbij zijn en waarvoor nog geen
// afgeronde workout_session bestaat. "Voorbij" = starts_at + duration_minutes
// ligt in het verleden, niet alleen starts_at (anders telt een sessie die nu
// nog loopt al mee als "niet gelogd").
//
// Review H1: lesdeelname alleen is niet genoeg. De schrijfpolicies eisen voor
// een gewone `coach` een coach_clients-rij met de cliënt, terwijl coach_id
// (aanmaker) en executing_coach_id (uitvoerder) sinds 2026-07-26 expliciet
// verschillende personen kunnen zijn. Een invallende coach zonder koppeling
// zag de sessie hier dus wél staan, maar liep bij het loggen tegen een harde
// RLS-fout — en kon het item nooit wegwerken. Daarom filtert deze functie
// nu ook op de daadwerkelijke koppeling; alleen admin_coach/superadmin slaan
// die eis over, precies zoals de RLS dat doet.
export async function fetchNietGelogdeSessies(coachId, role) {
  const nowMs = Date.now();
  const lookbackStart = new Date(nowMs - UNLOGGED_SESSION_LOOKBACK_DAYS * 24 * 3600 * 1000).toISOString();

  const lessonsRes = await supabase
    .from('group_lessons')
    .select('id, name, starts_at, duration_minutes, coach_id, executing_coach_id, coach:profiles!group_lessons_coach_id_fkey(full_name), executing_coach:profiles!group_lessons_executing_coach_id_fkey(full_name)')
    .eq('type', 'personal')
    .eq('is_cancelled', false)
    .gte('starts_at', lookbackStart)
    .lt('starts_at', new Date(nowMs).toISOString())
    .or(`coach_id.eq.${coachId},executing_coach_id.eq.${coachId}`)
    // L24: bij meer dan 200 afspraken zijn juist de oudste, langst vergeten
    // sessies het belangrijkst. De uiteindelijke UI-sortering hieronder
    // blijft nieuwste-eerst; dit bepaalt alleen welke 200 de grens halen.
    .order('starts_at', { ascending: true })
    .limit(200);
  throwIfError(lessonsRes);

  const pastLessons = (lessonsRes.data || []).filter(
    lesson => new Date(lesson.starts_at).getTime() + (lesson.duration_minutes || 0) * 60000 <= nowMs
  );
  if (pastLessons.length === 0) return [];

  const lessonIds = pastLessons.map(lesson => lesson.id);
  const [enrollRes, sessionsRes] = await Promise.all([
    supabase.from('lesson_enrollments')
      .select('lesson_id, client_id, client:profiles!lesson_enrollments_client_id_fkey(id, full_name)')
      .in('lesson_id', lessonIds).eq('status', 'enrolled'),
    supabase.from('workout_sessions')
      .select('lesson_id, client_id')
      .in('lesson_id', lessonIds).not('completed_at', 'is', null),
  ]);
  throwIfError(enrollRes);
  throwIfError(sessionsRes);

  const loggedKeys = new Set((sessionsRes.data || []).map(row => `${row.lesson_id}:${row.client_id}`));
  const lessonById = new Map(pastLessons.map(lesson => [lesson.id, lesson]));

  // Review H1: alleen cliënten waar deze coach ook echt aan gekoppeld is —
  // anders toont het kaartje werk dat de RLS hem verbiedt uit te voeren.
  let gekoppeldeClienten = null;
  if (!ROLLEN_ZONDER_KOPPELINGSEIS.includes(role)) {
    const koppelingRes = await supabase
      .from('coach_clients')
      .select('client_id')
      .eq('coach_id', coachId)
      .eq('status', 'active');
    throwIfError(koppelingRes);
    gekoppeldeClienten = new Set((koppelingRes.data || []).map(row => row.client_id));
  }

  return (enrollRes.data || [])
    .filter(enrollment => !loggedKeys.has(`${enrollment.lesson_id}:${enrollment.client_id}`))
    .filter(enrollment => gekoppeldeClienten === null || gekoppeldeClienten.has(enrollment.client_id))
    .map(enrollment => {
      const lesson = lessonById.get(enrollment.lesson_id);
      return {
        lessonId: enrollment.lesson_id,
        clientId: enrollment.client_id,
        clientName: enrollment.client?.full_name,
        lessonName: lesson?.name,
        startsAt: lesson?.starts_at,
        coachName: lesson?.executing_coach?.full_name || lesson?.coach?.full_name,
      };
    })
    .sort((a, b) => new Date(b.startsAt) - new Date(a.startsAt));
}

export async function fetchActiecentrumData(coachId, role) {
  const now = new Date().toISOString();
  const endOfToday = toAmsterdamISO(todayStr(), '23:59');
  const [tokensRes, signedRes, dismissedRes, pipelineRes, invoicesRes, requestsRes, nietGelogdeSessies] = await Promise.all([
    supabase.from('agreement_tokens')
      .select('id, client_id, created_by, rejected_by, token, created_at, expires_at, used_at, rejected_at, rejection_reason, client:profiles!agreement_tokens_client_id_fkey(id, full_name, email), membership_type:membership_types(name)')
      .order('created_at', { ascending: false }).order('id', { ascending: false }).limit(250),
    supabase.from('signed_agreements').select('client_id'),
    supabase.from('dismissed_actions').select('reference_id, action_type').eq('coach_id', coachId),
    supabase.from('prospect_pipeline')
      .select('prospect_id, stage, next_action_at, next_action_note')
      .neq('stage', 'afgerond').not('next_action_at', 'is', null).lte('next_action_at', endOfToday)
      .order('next_action_at', { ascending: true }).limit(100),
    supabase.from('invoices')
      .select('id, invoice_number, total_amount, client_id, client:profiles!invoices_client_id_fkey(id, full_name)')
      .eq('status', 'collections').order('created_at', { ascending: false }).limit(100),
    supabase.from('appointment_requests')
      .select('id, requested_at, reschedule_proposed_at, status, client:profiles!appointment_requests_client_id_fkey(id, full_name)')
      .eq('coach_id', coachId).in('status', ['pending', 'rescheduled'])
      .order('requested_at', { ascending: true }).limit(100),
    fetchNietGelogdeSessies(coachId, role),
  ]);

  [tokensRes, signedRes, dismissedRes, pipelineRes, invoicesRes, requestsRes].forEach(throwIfError);
  const signedClientIds = new Set((signedRes.data || []).map(row => row.client_id));
  const dismissed = new Set((dismissedRes.data || []).map(row => `${row.action_type}:${row.reference_id}`));
  const latestTokenByClient = new Map();
  for (const token of tokensRes.data || []) {
    if (!latestTokenByClient.has(token.client_id)) latestTokenByClient.set(token.client_id, token);
  }

  const afgewezenTokens = [];
  const verlopenTokens = [];
  for (const token of latestTokenByClient.values()) {
    if (signedClientIds.has(token.client_id) || token.used_at) continue;
    const rejectedByClient = token.rejected_at && token.rejected_by === token.client_id;
    if (rejectedByClient && !dismissed.has(`rejected_token:${token.id}`)) afgewezenTokens.push(token);
    else if (!token.rejected_at && token.expires_at < now && !dismissed.has(`expired_token:${token.id}`)) verlopenTokens.push(token);
  }

  const prospectIds = (pipelineRes.data || []).map(row => row.prospect_id);
  let prospectProfiles = [];
  if (prospectIds.length > 0) {
    const profilesRes = await supabase.from('profiles')
      .select('id, full_name, email, client_status, deleted_at')
      .in('id', prospectIds).eq('client_status', 'prospect').is('deleted_at', null);
    throwIfError(profilesRes);
    prospectProfiles = profilesRes.data || [];
  }
  const profileById = new Map(prospectProfiles.map(row => [row.id, row]));
  const prospectActies = (pipelineRes.data || [])
    .filter(row => profileById.has(row.prospect_id))
    .map(row => ({ ...row, prospect: profileById.get(row.prospect_id) }));

  return {
    afgewezenTokens,
    verlopenTokens,
    prospectActies,
    incasso: invoicesRes.data || [],
    ptAanvragen: requestsRes.data || [],
    nietGelogdeSessies,
  };
}

// Welke sleutels uit fetchActiecentrumData meetellen in de badge. Expliciete
// allowlist en géén Object.values (review M10): `nietGelogdeSessies` zit wel
// in de data — DashboardPage toont daar zijn eigen kaartje mee — maar staat
// bewust niet op /coach/acties (zie het besluit in BRIEFING §deel 5). Met een
// blinde telling zag de coach een badge "12", opende hij de pagina en telde
// er 4, zonder dat de rest ergens weg te werken viel. Deze lijst is één op
// één de werkvoorraad die ActiesPage daadwerkelijk rendert; een nieuw veld in
// de data belandt hierdoor ook niet meer automatisch in het getal.
export const ACTIECENTRUM_TELSLEUTELS = [
  'afgewezenTokens',
  'verlopenTokens',
  'prospectActies',
  'incasso',
  'ptAanvragen',
];

// Eén telling voor zowel de sidebar-badge als de kop van /coach/acties, zodat
// het getal en de zichtbare items per definitie niet uit elkaar kunnen lopen.
export function telOpenActies(data) {
  return ACTIECENTRUM_TELSLEUTELS.reduce((total, sleutel) => total + (data?.[sleutel]?.length || 0), 0);
}

export async function fetchOpenActiesCount(coachId, role) {
  const data = await fetchActiecentrumData(coachId, role);
  return telOpenActies(data);
}
